import type { StaffType } from "odyssey-shared";
import { say } from "../../words.ts";
import type { BadgeChange, BadgeOffer, BadgeReply } from "./client.ts";
import type { BadgeAsk } from "./command.ts";

/**
 * What the `/badge` command does on the page (ADR-112 decision 5.5, P1.9-T121): it exists only for a page the gateway
 * sent `staffBadgeOffer`, answers what is worn (the role's badge, never chosen) and whether it is hidden, sends the one change
 * the account asked for, hiding or showing, and words the answer and each refusal. A page with no offer is no staff page: `ask` says it holds no such command, and the chat
 * answers the word as it does any that is no command, sending nothing. A change that would alter nothing is answered on the page and
 * sent nowhere.
 */

/**
 * What the controller needs of the page: the change to the gateway, a line of information and a line for a failure.
 */
export interface BadgeControllerOptions {
  readonly change: BadgeChange;
  readonly note: (text: string) => void;
  readonly fail: (text: string) => void;
  /**
   * Whether the page has no account session (a development page joined by a token): an ended session is then answered
   * as that, not with "Log in again", since there is nothing to log in to.
   */
  readonly sessionless?: () => boolean;
}

/**
 * A staff type's name as the language files give it.
 *
 * @param type The type.
 * @returns Its name.
 */
export function staffTypeName(type: StaffType): string {
  return type === "guide_lead" ? say("staff.type.guideLead") : say(`staff.type.${type}`);
}

/**
 * The sentence for a change that did not happen.
 *
 * @param reply The reply.
 * @param sessionless Whether the page has no account session.
 * @returns The sentence.
 */
function refusal(reply: Exclude<BadgeReply, { readonly kind: "ok" }>, sessionless: boolean): string {
  switch (reply.kind) {
    case "session":
      return say(sessionless ? "staff.command.sessionless" : "staff.command.session");
    case "forbidden":
      return say("staff.command.forbidden");
    case "rate":
      return reply.waitSeconds === null ? say("staff.command.rateLater") : say("staff.command.rate", { seconds: reply.waitSeconds });
    case "unavailable":
      return say("staff.command.unavailable");
    case "failed":
      return say("staff.command.failed");
  }
}

/**
 * The `/badge` command's controller.
 */
export class BadgeController {
  private held: BadgeOffer | null = null;
  private generation = 0;
  private pending = false;

  /**
   * @param options The change, and where lines go.
   */
  public constructor(private readonly options: BadgeControllerOptions) {}

  /**
   * The offer the gateway last made, null before one.
   *
   * @returns The offer.
   */
  public get offer(): BadgeOffer | null {
    return this.held;
  }

  /**
   * Takes the gateway's offer: after `welcome` and after each change of the account's badge.
   *
   * @param offer The offer.
   */
  public readonly offered = (offer: BadgeOffer): void => {
    this.generation += 1;
    this.held = offer;
  };

  /**
   * Forgets the offer, as the end of a connection does.
   */
  public readonly ended = (): void => {
    this.generation += 1;
    this.held = null;
  };

  /**
   * Answers a `/badge` command.
   *
   * @param ask What it asks for.
   * @returns False when the page was offered nothing, so the command is none; true once it is dealt with.
   */
  public readonly ask = (ask: BadgeAsk): boolean => {
    const offer = this.held;
    if (offer === null) {
      return false;
    }
    switch (ask.kind) {
      case "usage":
        this.options.note(say("staff.command.usage"));
        return true;
      case "status":
        this.options.note(offer.hidden ? say("staff.command.wornHidden", { type: staffTypeName(offer.wears) }) : say("staff.command.worn", { type: staffTypeName(offer.wears) }));
        return true;
      case "hide":
      case "show": {
        if (this.pending) {
          return true;
        }
        const hidden = ask.kind === "hide";
        if (offer.hidden === hidden) {
          this.options.note(say(hidden ? "staff.command.hidden" : "staff.command.shown"));
          return true;
        }
        void this.send({ hidden }, hidden ? "staff.command.hidden" : "staff.command.shown");
        return true;
      }
    }
  };

  /**
   * Sends one change and words its answer. One change is in flight at a time, so a command typed again before the answer
   * sends nothing; an answer that lands after the offer changed or the connection ended leaves the offer as it is.
   *
   * @param choice The change.
   * @param done The key of the sentence an accepted change is answered with.
   */
  private async send(choice: { readonly hidden: boolean }, done: "staff.command.hidden" | "staff.command.shown"): Promise<void> {
    const generation = this.generation;
    this.pending = true;
    const reply = await this.options.change(choice).finally(() => {
      this.pending = false;
    });
    if (reply.kind !== "ok") {
      this.options.fail(refusal(reply, this.options.sessionless?.() === true));
      return;
    }
    if (generation === this.generation) {
      this.held = reply.offer;
    }
    this.options.note(say(done));
  }
}
